import { Link } from "react-router-dom";
import { AlertTriangle, ShieldOff, TrendingDown, KeyRound, Ban, Landmark, Waves, Building2 } from "lucide-react";
import SeoHead from "../components/SeoHead";

/**
 * RiskDisclosures — plain-English "how you can lose your money" appendix
 * linked from Terms § 5.9 ("No guarantees") and referenced by the token
 * website / whitepaper. Written to satisfy the exchange-listing review
 * bar for reasonable-consumer risk disclosure.
 *
 * Structural rules kept intentional:
 *   • Every risk is a scenario, not a legal term — "You could lose all
 *     of your $CAUL if X happens" — because a reviewer reads for
 *     comprehension, not statute cites.
 *   • Nothing here contradicts Terms § 5. It expands.
 *   • No marketing language. No upside claims. Anywhere.
 */
const Risk = ({ icon: Icon, title, children }) => (
  <div className="parchment p-6 space-y-2" data-testid={`risk-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-primary shrink-0 mt-1" />
      <div>
        <h2 className="font-serif text-2xl">{title}</h2>
        <div className="text-foreground/75 leading-relaxed text-sm mt-2 space-y-2">{children}</div>
      </div>
    </div>
  </div>
);

export default function RiskDisclosures() {
  return (
    <>
      <SeoHead
        title="$CAUL Risk Disclosures · Cauldron"
        description="Plain-English risks of holding, buying, or transferring the $CAUL utility token on the XRP Ledger. Read before acquiring $CAUL."
      />
      <div className="min-h-screen bg-background text-foreground py-16 px-6" data-testid="risk-disclosures-page">
        <div className="max-w-3xl mx-auto space-y-6">
          <header className="space-y-3">
            <Link to="/terms" className="text-xs uppercase tracking-wider text-secondary hover:underline">
              ← Back to Terms
            </Link>
            <div className="label-mystic text-secondary">Legal · Disclosures</div>
            <h1 className="font-serif text-5xl">$CAUL Risk Disclosures</h1>
            <p className="text-foreground/75 leading-relaxed">
              This page is a plain-English companion to Section 5 of our{" "}
              <Link to="/terms" className="text-secondary underline">Terms of Service</Link>.
              $CAUL is an XRPL <strong>utility token</strong> — not a security, not a bank deposit, not
              insured. Acquiring, holding, or transferring $CAUL exposes you to real risks of{" "}
              <strong>total, permanent, unrecoverable loss</strong>. Read this before you top up,
              accept rewards, or move $CAUL between wallets.
            </p>
            <div className="border border-primary/40 bg-primary/5 text-primary text-sm p-4 rounded-sm flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <strong className="uppercase tracking-wider text-[11px] block mb-1">Do not risk more than you can afford to lose.</strong>
                Nothing on Cauldron — including any statement by staff, ambassadors, or elders —
                is investment advice. If a friend, influencer, or website tells you $CAUL will "go
                up," treat that as noise, not a promise.
              </div>
            </div>
          </header>

          <Risk icon={TrendingDown} title="Market price risk">
            <p>
              $CAUL trades on the XRP Ledger&apos;s decentralized exchange (DEX). Its price is set
              by open-market supply and demand from third parties Cauldron does not control.
              The price <strong>can go to zero</strong>, can move sharply in minutes, and can stay
              at any value indefinitely. Historic prices, screenshots, and roadmap tokenomics
              are <strong>not</strong> predictions of future prices.
            </p>
          </Risk>

          <Risk icon={Waves} title="Liquidity risk">
            <p>
              The XRPL DEX order book for $CAUL can be thin. There may be periods when
              <strong> nobody is willing to buy at any price</strong>, or when the only bids are
              far below the last traded price. Cashing out through Cauldron&apos;s Stripe Bridge
              is a service feature, not a promise — it can be paused, delayed, throttled, or
              stopped entirely at Cauldron&apos;s discretion or by our banking partners.
            </p>
          </Risk>

          <Risk icon={KeyRound} title="Self-custody &amp; wallet-loss risk">
            <p>
              Any $CAUL you withdraw from Cauldron to a self-custodied XRPL wallet
              (Xaman or otherwise) is held by you, not by us. If you:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Lose your seed phrase or private key,</li>
              <li>Send $CAUL to a wrong address, unhosted address, or exchange without a $CAUL trustline,</li>
              <li>Are victim of a phishing site, malicious wallet app, or SIM-swap,</li>
              <li>Sign a malicious transaction from a compromised device,</li>
            </ul>
            <p>
              your $CAUL is <strong>gone</strong>. Cauldron has no ability to recover, reverse, or
              reissue lost or stolen funds — the XRP Ledger is final.
            </p>
          </Risk>

          <Risk icon={ShieldOff} title="Smart-contract, ledger, and infrastructure risk">
            <p>
              $CAUL relies on the XRP Ledger, our issuer wallet configuration, and third-party
              infrastructure (Xaman, node providers, DEX gateways, Stripe). Any of these can
              fail, be attacked, be censored by governments, or be shut down by their operators.
              A protocol-level bug, node consensus incident, or catastrophic key compromise
              could result in loss of funds even if you have done nothing wrong.
            </p>
          </Risk>

          <Risk icon={Ban} title="Issuer-power risk (freeze, blackhole, burn, reissue)">
            <p>
              Cauldron controls the $CAUL issuer wallet. To respond to critical incidents
              (e.g. a compromised key, court order, sanctions action, or ecosystem-breaking
              exploit) we may — to the extent permitted by the XRP Ledger&apos;s issuer
              settings at the time — <strong>freeze</strong>, <strong>blackhole</strong>,{" "}
              <strong>burn</strong>, or <strong>reissue</strong> $CAUL. See Terms § 5.7. These
              powers can materially and permanently change the value or availability of your
              $CAUL. Where possible we will communicate in advance, but critical incidents
              may require immediate action without notice.
            </p>
          </Risk>

          <Risk icon={Landmark} title="Regulatory &amp; jurisdictional risk">
            <p>
              Digital-asset regulation is unsettled and moves quickly. A regulator, court, or
              tax authority in your country — or in ours — could at any time restrict, tax, or
              outright ban the buying, holding, or transferring of $CAUL. Cauldron may be
              required to geofence you out of some or all $CAUL features. You are responsible
              for complying with your local laws, <strong>including tax reporting</strong>. If
              you don&apos;t know how $CAUL is taxed in your jurisdiction, get advice before
              you top up.
            </p>
          </Risk>

          <Risk icon={Building2} title="Sanctions, KYC, and account risk">
            <p>
              Cashouts to USDC via Stripe Bridge require KYC and AML/sanctions screening.
              If you cannot pass, do not pass, or are located in a sanctioned jurisdiction,
              Cauldron may pause or refuse cashouts and — where legally required — freeze the
              in-app custodial portion of your $CAUL balance. Suspected fraud, coordinated
              inauthentic behaviour, or violations of our{" "}
              <Link to="/community-guidelines" className="text-secondary underline">
                Community Guidelines
              </Link>{" "}
              can lead to clawback of $CAUL rewards <em>earned in-app</em> (see Terms § 5.6),
              though not $CAUL that has already left the platform.
            </p>
          </Risk>

          <Risk icon={AlertTriangle} title="Fraud &amp; social-engineering risk">
            <p>
              Cauldron staff will <strong>never</strong> DM you asking for your seed phrase,
              your password, your one-time code, remote access to your device, or a
              "verification" payment. Anyone doing so is impersonating us. Elders, ambassadors,
              and practitioners are also <strong>never</strong> authorised to ask for those
              things or to promise $CAUL price outcomes. Report impersonation immediately to{" "}
              <a href="mailto:security@thecauldron.app" className="text-secondary underline">
                security@thecauldron.app
              </a>
              .
            </p>
          </Risk>

          <div className="parchment p-6 border border-secondary/30">
            <div className="label-mystic text-secondary mb-2">Where to go from here</div>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-foreground/75">
              <li>
                Read the full{" "}
                <Link to="/terms" className="text-secondary underline">
                  Terms of Service § 5 — $CAUL Token
                </Link>{" "}
                for the legal framing.
              </li>
              <li>
                Read our{" "}
                <Link to="/privacy" className="text-secondary underline">
                  Privacy Policy
                </Link>{" "}
                for what data we collect around $CAUL activity.
              </li>
              <li>
                Concerned or suspect a compromise?{" "}
                <a href="mailto:security@thecauldron.app" className="text-secondary underline">
                  security@thecauldron.app
                </a>{" "}
                — we&apos;ll respond within 24 hours.
              </li>
            </ul>
          </div>

          <div className="text-[11px] text-foreground/50 pt-4">
            Last updated: <strong>{new Date().toISOString().slice(0, 10)}</strong>. This page is
            reviewed alongside every material change to Terms § 5. Nothing on this page is legal,
            tax, financial, or investment advice.
          </div>
        </div>
      </div>
    </>
  );
}
